import { CanMatchFn, Router, UrlSegment } from '@angular/router';
import { inject } from '@angular/core';
import { catchError, map, of } from 'rxjs';
import { OidcService } from '../oidc.service';
import { UserContext } from '@irian-ro/util-user';
import { HttpErrorResponse } from '@angular/common/http';
import { OIDC_BLOCKED_SEGMENTS } from '../auth-routes';

export const OidcIsLoggedInGuard: CanMatchFn = (_route, segments) => {
  const oidcService = inject(OidcService);
  const userContext = inject(UserContext);
  const router = inject(Router);

  // Defence in depth: never call `oidcService.login()` when the guard is matching an
  // auth-flow path. We use the canonical UrlSegment[] passed by the router rather than
  // `window.location` — the latter can lag a routing transition.
  const blockedSeg = OIDC_BLOCKED_SEGMENTS as readonly string[];
  const firstSeg = segments[0]?.path?.toLowerCase() ?? '';
  if (firstSeg && blockedSeg.indexOf(firstSeg) !== -1) {
    return false;
  }

  if (!oidcService.isAuthenticated) {
    // Path comes from the canonical segments (not window.location, which can lag a
    // routing transition). Query string is sourced from window.location since CanMatchFn
    // doesn't surface it; OidcService.login sanitises every blocked artefact at the write
    // site, so deep-link query params on a non-blocked path round-trip safely.
    const path = '/' + segments.map((s: UrlSegment) => s.path).join('/');
    const search = typeof window !== 'undefined' ? window.location.search : '';
    oidcService.login((path + search) || '/');
    return false;
  }

  return userContext.getLoggedInUserFromServerIfNotPresent(1000).pipe(
    map(() => true),
    catchError((err) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        oidcService.login('/');
      } else {
        router.navigate(['/error']);
      }
      return of(false);
    })
  );
};
